// Storage management for handling localStorage across app updates
const STORAGE_PREFIX = 'zippyhealth_';
const STORAGE_VERSION_KEY = `${STORAGE_PREFIX}storage_version`;
const CURRENT_STORAGE_VERSION = '1.0.0';

interface StorageItem<T = any> {
  key: string;
  value: T;
  timestamp: number;
  version: string;
}

interface StorageConfig {
  // Items that should persist across app updates
  persistentKeys: string[];
  // Items that should be cleared on version change
  versionSpecificKeys: string[];
  // Items that should expire after a certain time
  temporaryKeys: { [key: string]: number }; // key -> TTL in milliseconds
}

const defaultConfig: StorageConfig = {
  persistentKeys: [
    'user_preferences',
    'theme_settings',
    'language_preference',
    'accessibility_settings',
    'privacy_settings'
  ],
  versionSpecificKeys: [
    'auth_token',
    'user_session',
    'cached_data',
    'temp_state',
    'form_drafts'
  ],
  temporaryKeys: {
    'demo_data': 24 * 60 * 60 * 1000, // 24 hours
    'temp_cache': 60 * 60 * 1000,     // 1 hour
    'session_temp': 30 * 60 * 1000    // 30 minutes
  }
};

class StorageManager {
  private config: StorageConfig;
  private prefix: string;

  constructor(config: StorageConfig = defaultConfig) {
    this.config = config;
    this.prefix = STORAGE_PREFIX;
  }

  /**
   * Initialize storage management
   */
  public initialize(): void {
    this.migrateStorage();
    this.cleanupExpiredItems();
    this.updateStorageVersion();
  }

  /**
   * Set an item in localStorage with metadata
   */
  public setItem<T>(key: string, value: T, isTemporary: boolean = false): void {
    const fullKey = this.getFullKey(key);
    const storageItem: StorageItem<T> = {
      key: fullKey,
      value,
      timestamp: Date.now(),
      version: CURRENT_STORAGE_VERSION
    };

    try {
      localStorage.setItem(fullKey, JSON.stringify(storageItem));
    } catch (error) {
      console.error('Failed to set storage item:', error);
      // Handle quota exceeded error
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.clearOldestItems();
        try {
          localStorage.setItem(fullKey, JSON.stringify(storageItem));
        } catch (retryError) {
          console.error('Failed to set storage item after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Get an item from localStorage
   */
  public getItem<T = any>(key: string): T | null {
    const fullKey = this.getFullKey(key);
    
    try {
      const item = localStorage.getItem(fullKey);
      if (!item) return null;

      const storageItem = JSON.parse(item) as StorageItem<T>;
      
      // Check if item has expired
      if (this.isExpired(key, storageItem.timestamp)) {
        this.removeItem(key);
        return null;
      }

      return storageItem.value;
    } catch (error) {
      console.error('Failed to get storage item:', error);
      // Remove corrupted item
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Remove an item from localStorage
   */
  public removeItem(key: string): void {
    const fullKey = this.getFullKey(key);
    localStorage.removeItem(fullKey);
  }

  /**
   * Clear all app-related storage
   */
  public clearAll(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Clear version-specific storage while preserving persistent data
   */
  public clearVersionSpecific(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        const baseKey = this.getBaseKey(key);
        if (this.config.versionSpecificKeys.includes(baseKey)) {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Migrate storage from previous versions
   */
  private migrateStorage(): void {
    const currentVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    
    if (!currentVersion || currentVersion !== CURRENT_STORAGE_VERSION) {
      console.log('Migrating storage from version:', currentVersion, 'to:', CURRENT_STORAGE_VERSION);
      
      // Perform migration based on version differences
      this.performMigration(currentVersion);
    }
  }

  /**
   * Perform migration based on version differences
   */
  private performMigration(fromVersion: string | null): void {
    if (!fromVersion) {
      // First time user, no migration needed
      return;
    }

    // Example migration logic
    if (fromVersion < '1.0.0') {
      // Migrate from pre-1.0.0 version
      this.migrateFrom_0_x_x();
    }

    // Add more migration logic as needed
  }

  /**
   * Migration from version 0.x.x
   */
  private migrateFrom_0_x_x(): void {
    // Clear old auth tokens that might be incompatible
    const oldAuthKeys = ['auth_token', 'user_session', 'refresh_token'];
    oldAuthKeys.forEach(key => {
      localStorage.removeItem(key);
      localStorage.removeItem(this.getFullKey(key));
    });
  }

  /**
   * Clean up expired items
   */
  private cleanupExpiredItems(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const storageItem: StorageItem<unknown> = JSON.parse(item);
            const baseKey = this.getBaseKey(key);
            
            if (this.isExpired(baseKey, storageItem.timestamp)) {
              keysToRemove.push(key);
            }
          }
        } catch (error) {
          // Remove corrupted items
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Check if an item has expired
   */
  private isExpired(baseKey: string, timestamp: number): boolean {
    const ttl = this.config.temporaryKeys[baseKey];
    if (!ttl) return false;
    
    return (Date.now() - timestamp) > ttl;
  }

  /**
   * Clear oldest items when storage is full
   */
  private clearOldestItems(): void {
    const items: { key: string; timestamp: number }[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const storageItem: StorageItem<unknown> = JSON.parse(item);
            const baseKey = this.getBaseKey(key);
            
            // Don't remove persistent items
            if (!this.config.persistentKeys.includes(baseKey)) {
              items.push({ key, timestamp: storageItem.timestamp });
            }
          }
        } catch (error) {
          // Remove corrupted items
          localStorage.removeItem(key);
        }
      }
    }
    
    // Sort by timestamp and remove oldest items
    items.sort((a, b) => a.timestamp - b.timestamp);
    const itemsToRemove = items.slice(0, Math.ceil(items.length * 0.3)); // Remove 30%
    
    itemsToRemove.forEach(item => {
      localStorage.removeItem(item.key);
    });
  }

  /**
   * Update storage version
   */
  private updateStorageVersion(): void {
    localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION);
  }

  /**
   * Get full key with prefix
   */
  private getFullKey(key: string): string {
    return key.startsWith(this.prefix) ? key : `${this.prefix}${key}`;
  }

  /**
   * Get base key without prefix
   */
  private getBaseKey(fullKey: string): string {
    return fullKey.startsWith(this.prefix) ? fullKey.substring(this.prefix.length) : fullKey;
  }

  /**
   * Get storage statistics
   */
  public getStorageStats(): { used: number; available: number; items: number } {
    let used = 0;
    let items = 0;
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        const item = localStorage.getItem(key);
        if (item) {
          used += item.length;
          items++;
        }
      }
    }
    
    // Estimate available space (most browsers have ~5-10MB limit)
    const estimated_limit = 5 * 1024 * 1024; // 5MB
    const available = estimated_limit - used;
    
    return { used, available, items };
  }
}

export const storageManager = new StorageManager();
export default storageManager; 
